import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "./shared";
import {
  SCHOOL_COLOR, PORTFOLIO_COLOR, flowState, compareState, periodsForSchool,
  openPortfolioAssessment, assessmentActionLabel,
} from "./portfolioFlow";

// Latest period per school → "Ready to compare" (final first) + "Needs your assessment".
const buildLists = (periods) => {
  const tenants = new Map();
  (periods || []).forEach((p) => (p.requests || []).forEach((q) => q.tenant_id && tenants.set(q.tenant_id, q.school_name)));
  const ready = [], needs = [];
  tenants.forEach((name, tid) => {
    const e = periodsForSchool(periods, tid)[0];
    if (!e) return;
    const s = flowState(e.req.status), p = flowState(e.req.portfolio_status);
    const included = !!e.period.enable_portfolio_assessments;
    const cs = compareState({ school: s, portfolio: p, included });
    const item = { tid, name: name || e.req.school_name || "School", period: e.period, s, p };
    if (cs.enabled) ready.push({ ...item, tag: cs.tag });
    if (included && p !== "final") needs.push(item);
  });
  ready.sort((a, b) => (a.tag === b.tag ? a.name.localeCompare(b.name) : a.tag === "Ready" ? -1 : 1));
  needs.sort((a, b) => a.name.localeCompare(b.name));
  return { ready, needs };
};

const List = ({ title, color, items, empty, testid, render }) => (
  <div className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white" data-testid={testid}>
    <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      <span className="ml-auto text-xs text-slate-500">{items.length}</span>
    </div>
    {items.length ? <ul className="divide-y divide-slate-100">{items.map(render)}</ul>
      : <p className="px-4 py-5 text-sm text-slate-500">{empty}</p>}
  </div>
);

export const DashboardTodo = ({ pid }) => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState(null);
  useEffect(() => {
    if (!pid) return;
    fetch(`${API}/api/portfolio/${pid}/periods`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { periods: [] })).then((d) => setPeriods(d.periods || [])).catch(() => setPeriods([]));
  }, [pid]);
  const { ready, needs } = useMemo(() => buildLists(periods), [periods]);
  if (!periods || !periods.length) return null;
  const start = (it) => openPortfolioAssessment(pid, it.period.id, it.tid).catch((e) => toast.error(e.message));
  return (
    <div className="flex flex-col lg:flex-row gap-4" data-testid="portfolio-todo">
      <List title="Ready to compare" color={SCHOOL_COLOR} items={ready} testid="todo-ready-to-compare" empty="Nothing to compare yet."
        render={(it) => (
          <li key={it.tid} className="flex items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 break-words">{it.name}</div>
              <div className="truncate text-xs text-slate-500">{it.period.name}</div>
            </div>
            <button onClick={() => navigate(`/portfolio/compare/${it.period.id}/${it.tid}?pid=${pid}`)} data-testid={`todo-compare-${it.tid}`}
              className="msx-todo-btn inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md px-2 text-[13px] font-semibold text-white" style={{ background: SCHOOL_COLOR }}>
              <ArrowLeftRight className="h-3.5 w-3.5 shrink-0" /> Compare
              <span className={`w-[78px] shrink-0 rounded-full text-center text-[10.5px] font-semibold ${it.tag === "Provisional" ? "bg-white text-amber-800" : "bg-white/20 text-white ring-1 ring-white/40"}`}
                data-testid={`todo-compare-tag-${it.tid}`}>{it.tag || "Ready"}</span>
            </button>
          </li>
        )} />
      <List title="Needs your assessment" color={PORTFOLIO_COLOR} items={needs} testid="todo-needs-assessment" empty="You're up to date."
        render={(it) => (
          <li key={it.tid} className="flex items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 break-words">{it.name}</div>
              <div className="truncate text-xs text-slate-500">{it.period.name}</div>
            </div>
            <button onClick={() => start(it)} data-testid={`todo-assess-${it.tid}`}
              className="msx-todo-btn inline-flex shrink-0 items-center justify-center rounded-md px-2 text-[13px] font-semibold text-white" style={{ background: PORTFOLIO_COLOR }}>
              {assessmentActionLabel(it.p).replace(" your assessment", " yours")}
            </button>
          </li>
        )} />
    </div>
  );
};
